import React from "react";
import APPCONFIG from "../../constants/Config";
import moment from "moment";
import {cashDiscount} from "../../constants/Utilities";
import {
	ReceiptTaxSection,
	ReceiptSummaryRow,
	locationDefaultRateFromObject,
	cumulativeCashRefundedFromTaxJson,
	originalTaxTotalFromTaxJson,
} from "./ReceiptInvoiceLayout";
// Wrapper around ReactToPrint that works around its pattern of passing anonymous functions in the render method

// This receipt re-applies the per-line cash discount for display purposes and renders the tax buckets that were
// frozen on the transaction's `tax_json` snapshot. The canonical "checkout totals" order of operations lives in
// `Checkout.recalculateTotals` (frontend, JSDoc) and `\Abstracts\Utilities::buildSectionFromLines` (backend);
// keep any math here aligned with that spec.
export default (data) => {
	const pickTradeUnitPrice = (item) => {
		const candidates = [
			item?.price,
			item?.trade_price,
			item?.trade_amount,
			item?.trades?.price,
			item?.trades?.cost,
			item?.cost,
		];
		const nonZero = candidates
			.map((v) => Number(v))
			.find((n) => Number.isFinite(n) && n !== 0);
		if (Number.isFinite(nonZero)) return Math.abs(nonZero);
		const fallback = candidates
			.map((v) => Number(v))
			.find((n) => Number.isFinite(n));
		return Number.isFinite(fallback) ? Math.abs(fallback) : 0;
	};

	let sub_total = 0;
	let tax_total = 0;
	let refund_total = 0;
	// let newTotal = 0;
	let singularItemDiscounts = 0;
	let otherFieldsObject = data?.receiptData;
	let tradeSubtotal = 0;
	const customerName = otherFieldsObject?.customers?.name || otherFieldsObject?.client || null;
	const customerPhone = otherFieldsObject?.customers?.phone || null;
	const customerEmail = otherFieldsObject?.customers?.email || otherFieldsObject?.receipt_email || null;
	const hasCustomerInfo = !!(customerName || customerPhone || customerEmail);

	let items = data.items.map((item, i) => {
		let trade = false;
		let _discounted_unit_price = null
		let _discount_amount = null;
		let row_subtotal = null;

		//if trade-in
		if (item.transaction_type === 'transaction_trade') {
			const tradeUnitPrice = pickTradeUnitPrice(item);
			const tradeQtyRaw = Number(item?.quantity);
			const tradeQty = Number.isFinite(tradeQtyRaw) && tradeQtyRaw !== 0 ? Math.abs(tradeQtyRaw) : 1;
			row_subtotal = tradeUnitPrice * tradeQty;
			tradeSubtotal += row_subtotal;
			trade = true;
		}

		//Determine quantity
		let quantity = 1; // default fallback
		if (item.transaction_type === "transaction_product" && item.refund_quantity != null && trade === false) {
			quantity = 0;
		} else if (item.refund_quantity != null && trade === false) {
			quantity = item.quantity - item.refund_quantity;
		} else if (item.quantity !== null) {
			quantity = item.quantity;
		}

		let price = parseFloat(item.price);
		if (!trade) {
			if (
				item.transactions?.cash_discount_amount && item.transactions?.cash_discount_amount !== "0.00" &&
				item.transactions?.cash_discount_percentage
			) {
				const {discount_amount, discounted_unit_price} = cashDiscount(
					item.transactions.cash_discount_percentage,
					quantity,
					price
				)

				_discounted_unit_price = discounted_unit_price;

				const discountedSubtotal =
					(price * quantity) - discount_amount

				_discount_amount = discount_amount
				row_subtotal = discountedSubtotal;
			} else {
				row_subtotal = price * quantity;
			}
			sub_total += (price * quantity);

		}
		tax_total = parseFloat(item.transactions.tax_amount);
		if (item.refund_quantity > 0) {
			refund_total += parseFloat(item.refund_amount);
		}
		// newTotal =  (sub_total - refund_total + tax_total);

		//Getting singular item discounts from entries
		singularItemDiscounts += (parseFloat(item.discount_amount));

		return (
			<div>
				<br></br>
				<div key={'i' + i} style={{borderBottom: "1px dashed #d6d6d6"}} id={"printItems"}>
					<div className={"row"}>
						<span className={"col-6"}>
							<span><strong>{item.name}{trade ? " - Trade" : ''}</strong></span>
						</span>
						<span className={"col-6 text-right"}>
							<span id={"printPrice"}>${price.toFixed(2)}</span>
						</span>
					</div>

					<div className={"row"}>
						<span className={"col-6"}>
							{
								item.serial_number ?
									<span><strong>{item.serial_number}</strong></span> :
									<span>-</span>
							}
						</span>
						<span className={"col-6 text-right"}>
							<span id={"printQuantity"}>x{quantity}</span>
						</span>
					</div>

					{(item?.discount_amount != "0.00" || _discounted_unit_price) ?
						<>
							<div className={"row"}>
								<span className={"col-6"}>
									<span><strong> Sub Total: </strong></span>
								</span>
								<span className={"col-6 text-right"}>
									<span>${item.price * quantity}</span>
								</span>
							</div>

							{_discounted_unit_price ? <div className={"row"}>
								<span className={"col-6"}>
									<span><strong> Cash Discount: </strong></span>
								</span>
								<span className={"col-6 text-right"}>
									<span>- $ {parseFloat(_discount_amount).toFixed(2)}</span>
								</span>
							</div> : null}

							{item?.discount_amount != "0.00" ? <div className={"row"}>
								<span className={"col-6"}>
									<span><strong> Discount: </strong></span>
								</span>
								<span className={"col-6 text-right"}>
									<span>- $ {parseFloat(item.discount_amount).toFixed(2)}</span>
								</span>
							</div> : null}
						</> : null
					}

					<div className={"row"}>
						<span className={"col-6"}>
							{item.sku}
						</span>
						<span className={"col-6 text-right"}>
							<span id={"printRefunded"}>
								<strong>{trade ? "- " : ''}{!item.refunded ? "$" + ((row_subtotal - item.discount_amount).toFixed(2)) : "Refunded"}</strong>
							</span>
						</span>
					</div>
					<br></br>
				</div>
			</div>
		)
	})

	let location = data.data[0].transactions.locations;
	let transaction = data.data[0].transactions;
	const metaRefund = cumulativeCashRefundedFromTaxJson(transaction.tax_json);
	if (metaRefund != null && metaRefund >= 0.01) {
		refund_total = metaRefund;
	}
	tax_total = originalTaxTotalFromTaxJson(transaction.tax_json, tax_total);
	let customReceipt = data.rData.items[0];
	let receiptDate = moment(data.data[0].transactions.created).format('MM-DD-YYYY');

	//Grab discount field from response object
	let discountAmount = data.data[0].transactions.discount_amount;
	let discountPercent = data.data[0].transactions.discount_percentage;

	//amount
	let amount = data.data[0].transactions.amount;

	//Set up discount fields for receipt
	let discountField = null;
	let discountedTotal = null;

	if (discountAmount > 0) {
		discountField = "$" + discountAmount;
		discountedTotal = (amount - tax_total);
		discountedTotal.toFixed(2);
	}
	if (discountPercent > 0) {
		discountField = "$" + (parseFloat((discountPercent / 100)) * (parseFloat(sub_total) - parseFloat(tradeSubtotal) - parseFloat(singularItemDiscounts))).toFixed(2) + " (" + discountPercent + "%)";
		discountedTotal = (amount) - tax_total - parseFloat(transaction.cc_fee_amount).toFixed(2);
		discountedTotal.toFixed(2);
	}

	//Logic for determining change on receipt
	let remaining = transaction.amount;
	if (!isNaN(transaction.cash_amount) && transaction.cash_amount) {
		remaining -= parseFloat(transaction.cash_amount).toFixed(2);
	}
	if (!isNaN(transaction.check_amount) && transaction.check_amount) {
		remaining -= parseFloat(transaction.check_amount).toFixed(2);
	}
	if (!isNaN(transaction.zelle_amount) && transaction.zelle_amount) {
		remaining -= parseFloat(transaction.zelle_amount).toFixed(2);
	}
	if (!isNaN(transaction.other_amount) && transaction.other_amount) {
		remaining -= parseFloat(transaction.other_amount).toFixed(2);
	}
	if (!isNaN(transaction.invoice_amount) && transaction.invoice_amount) {
		remaining -= parseFloat(transaction.invoice_amount).toFixed(2);
	}
	remaining = parseFloat(remaining).toFixed(2);
	let change = (remaining < 0) ? remaining * -1 : 0;

	const taxableMerchApprox =
		parseFloat(amount) -
		parseFloat(tax_total) -
		parseFloat(transaction.cc_fee_amount || 0);
	const locationDefaultRate = locationDefaultRateFromObject(location);

	return (
		<>
			<div>
				{location ?
					<div style={{marginTop: "1rem"}}>
						<span style={{textAlign: "center", fontSize: "1.2rem"}}>
							{customReceipt.logo &&
								<div>
									<img
										id="logo"
										src={APPCONFIG.settings.apiURL() + '/' + APPCONFIG.settings.apiVersion + '/accounts/customizations/image'}
										alt={""}
										style={{marginBottom: "1rem", height: "115px"}}
									/>
								</div>
							}
							<div>
								{location.name ? <div><b>{location.name}</b></div> : null}
								{location.store_number ? <div>{location.store_number}</div> : null}
								{location.phone ? <div>{location.phone}</div> : null}
								{location.email ? <div>{location.email}</div> : null}
								{location.street_1 ? <div>{location.street_1}</div> : null}
								{location.street_1 ?
									<div>{location.city} {location.state} {location.zip}</div> : null}
								{receiptDate}
								<br/><br/>
							</div>
						</span>

					</div>
					: null}
				<div style={{fontSize: '20px', borderBottom: "1px solid #d6d6d6", marginTop: "1rem", paddingBottom: "10px"}}>
					<b>Transaction {data.data[0].transaction}</b>
				</div>
				<div>
					{items}
				</div>
				<div className={"row"} style={{color: "rgba(0, 0, 0, 0.5)", paddingTop: "14px"}}>
					<div className={"col"} id={"poInfo"}>
						{/*Conditionally show transaction information*/}
						{otherFieldsObject.order_po_number ?
							<div>
								<span>PO Number</span>
								<span style={{float: "right"}}>{otherFieldsObject.order_po_number}</span>
								<br/>
							</div> : null
						}
						{transaction.transaction_id ?
							<div>
								<span>ID</span>
								<span style={{float: "right"}}>{transaction.transaction_id}</span>
								<br/>
							</div> : null
						}
						{transaction.terminal_last_four ?
							<div>
								<span>Last 4 of CC</span>
								<span style={{float: "right"}}>{transaction.terminal_last_four}</span>
								<br/>
							</div> : null
						}
					</div>
				</div>
				{!transaction.order_po_number && !transaction.transaction_id && !transaction.terminal_last_four ? null : <hr></hr>}
				<div className="invoice-sum text-right">
					<ul className="list-unstyled">
						<ReceiptSummaryRow label="Sub Total:" amount={`$ ${sub_total.toFixed(2)}`} />
						{transaction.cash_discount_amount && transaction.cash_discount_amount !== "0.00" && transaction.cash_discount_percentage && (
							<ReceiptSummaryRow
								label={`Cash Discount (${transaction.cash_discount_percentage}%):`}
								right={<span>- $ {parseFloat(transaction.cash_discount_amount).toFixed(2)}</span>}
							/>
						)}
						{singularItemDiscounts ? (
							<ReceiptSummaryRow
								label="Singular Discounts:"
								amount={`- $ ${parseFloat(singularItemDiscounts).toFixed(2)}`}
							/>
						) : null}
						{tradeSubtotal ? (
							<ReceiptSummaryRow
								label="Trade-In Subtotal:"
								amount={`- $ ${parseFloat(tradeSubtotal).toFixed(2)}`}
							/>
						) : null}
						{discountField ? (
							<ReceiptSummaryRow label="Transaction Discount:" amount={discountField} />
						) : null}
						{discountField ? (
							<ReceiptSummaryRow
								label="Discounted Sub Total:"
								amount={`$ ${discountedTotal.toFixed(2)}`}
								strong
							/>
						) : null}
						<ReceiptTaxSection
							taxJson={transaction.tax_json}
							taxAmount={tax_total}
							taxableMerchandiseApprox={taxableMerchApprox}
							defaultRatePercent={locationDefaultRate}
							showTaxBreakdown={data.props.show_tax_breakdown}
						/>
						{transaction.cc_fee_amount > 0 && (
							<ReceiptSummaryRow label="CC Fee:" amount={`$ ${parseFloat(transaction.cc_fee_amount).toFixed(2)}`} />
						)}
						<ReceiptSummaryRow label="Total:" amount={`$ ${amount}`} strong />

						{/*Refund section of receipt*/}
						{
							refund_total > 0 ? (
								<li id="refunded">
									<strong>Total refunded: - $ {(parseFloat(refund_total)).toFixed(2)}</strong>
								</li>
							) : null
						}
						<hr id={"hrPrint"}/>

						{/*Payment section of receipt*/}
						{
							transaction.cash_amount > 0 ? (
								<li>
									<strong>Cash: ${transaction.cash_amount}</strong>
								</li>
							) : null
						}
						{
							transaction.check_amount > 0 ? (
								<li>
									<strong>Check: ${transaction.check_amount}</strong>
								</li>
							) : null
						}
						{
							transaction.zelle_amount > 0 ? (
								<li>
									<strong>Zelle: ${transaction.zelle_amount}</strong>
								</li>
							) : null
						}
						{
							transaction.other_amount > 0 ? (
								<li>
									<strong>Gift Cert.: ${transaction.other_amount}</strong>
								</li>
							) : null
						}
						{
							transaction.invoice_amount > 0 ? (
								<li>
									<strong>To Invoice: ${transaction.invoice_amount}</strong>
								</li>
							) : null
						}
						{
							transaction.processor_amount > 0 ? (
								<li>
									<strong>{transaction.is_debit ? 'Debit Card' : 'Card'}: ${transaction.processor_amount}</strong>
								</li>
							) : null
						}
						{/*If customer paid with cash, this is their change*/}
						{change > 0 ? <hr/> : null}
						{
							change > 0 ? (
								<li>
									<strong>Change: ${change.toFixed(2)}</strong>
								</li>
							) : null
						}
					</ul>
				</div>


				{/*Don't show line if there is no transaction info*/}
				{!customReceipt.message && !customReceipt.warranty && !customReceipt.return_policy ? null : <hr></hr>}
				<div id="customMessageContainer">
					<div style={{textAlign: "center"}}>
						<h4 id="customMessage">
							{customReceipt.message ?? ""}
						</h4>
						{customReceipt.warranty &&
							<div style={{textAlign: "center"}}>
								<span><b>Warranty Information</b></span>
								<div>
									{customReceipt.warranty}
								</div>
								<br/>
								<br/>
							</div>
						}
						{customReceipt.return_policy &&
							<div style={{textAlign: "center"}}>
								<span><b>Returns Information</b></span>
								<div>
									{customReceipt.return_policy}
								</div>
								<br/>
								<br/>
							</div>
						}
					</div>
				</div>

				{/*Section for Other Transaction information regarding the customer/client*/}
				{data.props.show_customer_info ?
					<div>
						<hr/>
						<div style={{textAlign: "center"}}>
							<div style={{fontSize: "1.2rem"}}><b>Customer Information</b></div>
							{hasCustomerInfo ? (
								<>
									{customerName ? <div>{customerName}</div> : null}
									{customerPhone ? <div>{customerPhone}</div> : null}
									{customerEmail ? <div>{customerEmail}</div> : null}
								</>
							) : (
								<div><i>There is no customer info to display.</i></div>
							)}
							<br/>
						</div>
						{otherFieldsObject.description ?
							<div style={{textAlign: "center"}}>
								<span><b>Description</b></span>
								<div>
									{otherFieldsObject.description}
								</div>
								<br/>
							</div>
							: null}
						{otherFieldsObject.notes ?
							<div style={{textAlign: "center"}}>
								<span><b>Notes</b></span>
								<div>
									{otherFieldsObject.notes}
								</div>
								<br/>
							</div>
							: null}
					</div> : null}

			</div>
		</>
	)
}
